'use strict'
/**
 * BottomNav · barra FLOTANTE estilo Instagram (cincelado founder 18-jun).
 * ════════════════════════════════════════════════════════════════════════════
 * Comportamiento (la novedad · no solo los iconos):
 *   · FLOTANTE   · no anclada al fondo · flota con margen + forma de píldora.
 *   · GLASS      · fondo translúcido cálido (cream rgba) + backdrop-blur · se ve
 *                  el contenido pasar por detrás. NO un bloque opaco · NO cristal frío.
 *   · TAP + DESLIZABLE · tap directo al tab, O pulsar y arrastrar horizontal para
 *                  recorrer los tabs (el indicador-píldora sigue el dedo · al soltar,
 *                  navega al tab bajo el dedo).
 *   · REACTIVA AL SCROLL · al bajar se ENCOGE (discreta) · al subir se RE-EXPANDE.
 *
 * API IDÉNTICA a la versión anterior (items/activeId/onSelect/showLabels) → cero
 * regresión de navegación · todas las páginas siguen llamándola igual.
 * Cata founder a 390px: el TACTO del deslizar + el scroll-react en iPhone real.
 *
 * aurIA es FAB separado (FabAuria) · NO va en la nav.
 */

import React, { useEffect, useRef, useState } from 'react'
import { NavToday, NavHub, NavCalendar, NavNutrition, NavProgress } from '../icons/navigation'

// LA PIEL F2 · 5 pestañas v3 (labels español, siempre visibles · terracota activo).
// DOMINIOS murió como tab (su contenido se fusiona en Progreso · F7); nace NUTRICIÓN,
// la casa del dominio cross donde orbita el engagement. aurIA = FabAuria separado.
export const DEFAULT_ITEMS = [
  { id: 'today',     icon: NavToday,     label: 'Hoy',       path: '/today' },
  { id: 'hub',       icon: NavHub,       label: 'Hub',       path: '/hub' },
  { id: 'calendar',  icon: NavCalendar,  label: 'Plan',      path: '/calendar' },
  { id: 'nutricion', icon: NavNutrition, label: 'Nutrición', path: '/nutricion' },
  { id: 'progress',  icon: NavProgress,  label: 'Progreso',  path: '/progress' },
]

export default function BottomNav({
  items = DEFAULT_ITEMS,
  activeId,
  onSelect,
  showLabels = true,   // LA PIEL F2 · v3 anula la doctrina "sin labels": label SIEMPRE visible
}) {
  const n = Math.max(1, items.length)
  // -1 = ninguna pestaña activa (ruta que no es tab · ej. /mobility) → sin píldora.
  const activeIndex = items.findIndex((it) => it.id === activeId)
  const [compact, setCompact] = useState(false)
  const [dragIndex, setDragIndex] = useState(null)
  const barRef = useRef(null)
  const didDragRef = useRef(false)
  const downRef = useRef(false)

  // ── Reactiva al scroll · captura scroll de window Y de contenedores internos ──
  useEffect(() => {
    let lastY = 0
    let ticking = false
    const read = (e) => {
      const y = (e?.target?.scrollTop ?? window.scrollY ?? 0)
      const dy = y - lastY
      if (Math.abs(dy) > 6) {
        // baja y hay recorrido → encoge · sube o cerca del top → expande
        if (dy > 0 && y > 48) setCompact(true)
        else if (dy < 0) setCompact(false)
        lastY = y
      }
      ticking = false
    }
    const onScroll = (e) => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => read(e))
    }
    // capture:true atrapa el scroll de cualquier elemento (las páginas scrollean en divs internos)
    window.addEventListener('scroll', onScroll, { passive: true, capture: true })
    return () => window.removeEventListener('scroll', onScroll, { passive: true, capture: true })
  }, [])

  const indicatorIndex = dragIndex != null ? dragIndex : activeIndex

  function indexFromClientX(clientX) {
    const el = barRef.current
    if (!el) return activeIndex
    const r = el.getBoundingClientRect()
    const rel = clientX - r.left
    return Math.min(n - 1, Math.max(0, Math.floor(rel / (r.width / n))))
  }

  function onPointerDown(e) {
    downRef.current = true
    didDragRef.current = false
    try { e.currentTarget.setPointerCapture?.(e.pointerId) } catch {}
  }
  function onPointerMove(e) {
    if (!downRef.current) return
    const idx = indexFromClientX(e.clientX)
    if (idx !== activeIndex) didDragRef.current = true
    setDragIndex(idx)
  }
  function onPointerUp(e) {
    if (!downRef.current) return
    downRef.current = false
    if (didDragRef.current) {
      const idx = indexFromClientX(e.clientX)
      const it = items[idx]
      if (it) onSelect?.(it.id, it)
    }
    setDragIndex(null)
  }

  const iconSize = compact ? 21 : 25
  const padV = compact ? 6 : 9
  const pillPct = 100 / n

  return (
    <nav
      role="navigation"
      aria-label="Navegación principal"
      style={{
        position: 'fixed',
        left: 0, right: 0,
        bottom: 'calc(var(--pl-safe-bottom, 0px) + 12px)',
        display: 'flex', justifyContent: 'center',
        paddingLeft: 'var(--pl-safe-left)', paddingRight: 'var(--pl-safe-right)',
        zIndex: 'var(--pl-z-sticky)',
        fontFamily: 'var(--pl-font-sans)',
        pointerEvents: 'none', // el wrapper no captura · solo la píldora
      }}
    >
      <div
        ref={barRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        style={{
          pointerEvents: 'auto',
          position: 'relative',
          width: 'min(420px, calc(100vw - 32px))',
          // GLASS cálido · translúcido + blur (no opaco, no cristal frío)
          // rgba directo (cream #FAF9F5 · terracota #A65C46) por compatibilidad con
          // webviews que aún no soportan color-mix (iOS antiguo) → cero glass roto.
          background: 'rgba(250, 249, 245, 0.72)',
          backdropFilter: 'blur(20px) saturate(1.4)',
          WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
          border: '1px solid rgba(166, 92, 70, 0.22)',
          borderRadius: 999,
          boxShadow: '0 8px 28px rgba(75, 56, 38, 0.16), 0 2px 6px rgba(75,56,38,0.08)',
          touchAction: 'pan-y', // permite scroll vertical · gestiona el drag horizontal
          transition: 'padding var(--pl-dur-fast) var(--pl-ease-out)',
          padding: `${padV}px 6px`,
        }}
      >
        {/* Indicador-píldora que sigue al activo / al dedo · oculto si no hay tab activa */}
        {indicatorIndex >= 0 && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: padV, bottom: padV,
            left: `calc(${indicatorIndex * pillPct}% + 6px)`,
            width: `calc(${pillPct}% - 12px)`,
            background: 'rgba(166, 92, 70, 0.13)',
            borderRadius: 999,
            transition: 'left var(--pl-dur-base, 240ms) var(--pl-ease-out), top var(--pl-dur-fast) var(--pl-ease-out), bottom var(--pl-dur-fast) var(--pl-ease-out)',
            pointerEvents: 'none',
          }}
        />
        )}
        <ul style={{
          position: 'relative',
          display: 'flex', alignItems: 'center', justifyContent: 'space-around',
          listStyle: 'none', margin: 0, padding: 0,
        }}>
          {items.map((it, i) => {
            const Icon = it.icon
            const active = i === indicatorIndex
            return (
              <li key={it.id} style={{ flex: 1 }}>
                <button
                  type="button"
                  aria-label={it.label}
                  aria-current={it.id === activeId ? 'page' : undefined}
                  onClick={() => { if (!didDragRef.current) onSelect?.(it.id, it) }}
                  style={{
                    width: '100%',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    background: 'transparent', border: 'none',
                    color: active ? 'var(--pl-terracota)' : 'var(--pl-muted)',
                    cursor: 'pointer',
                    padding: '6px 4px',
                    gap: 3,
                    transition: 'color var(--pl-dur-fast) var(--pl-ease-out), transform var(--pl-dur-fast) var(--pl-ease-out)',
                    transform: active ? 'translateY(-1px)' : 'none',
                  }}
                >
                  <Icon size={iconSize} strokeWidth={active ? 2 : 1.6} />
                  {showLabels && !compact && (
                    <span style={{ fontSize: 11, fontWeight: active ? 600 : 500, letterSpacing: '0.02em' }}>
                      {it.label}
                    </span>
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      </div>
    </nav>
  )
}
